import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";

import { Icon } from "@majourneys/icons";

import { menuKeys, useDocumentDir, useDropdown, useNarrowViewport, useRoving } from "./keys";
import { useMenuStyles } from "./menu-styles";
import {
  EmptyRow,
  MenuLabel,
  MenuRule,
  MenuScroll,
  MenuSurface,
  MenuTrigger,
  type MenuTriggerRenderProps,
} from "./parts";
import { usePresence, type MenuGroup } from "./presence";
import { MUTED, NAVY, RED, ROW_ACTIVE, ROW_CURRENT } from "./tokens";

/**
 * A menu whose rows do something rather than mean something.
 *
 * Three things separate it from the choice menus. Rows carry a second line, so
 * a conversation shows when it was last touched. A row can have its own handle
 * on the end, which is a second button and therefore a sibling rather than a
 * child, because a button inside a button is not a button. And that handle asks
 * before it destroys anything, in the menu, where the row is — not through a
 * browser dialog dropped over the middle of the concierge.
 */

export interface ActionMenuRemove {
  readonly label: string;
  /** Asked in the row itself. Short: it has one line to live on. */
  readonly question: string;
  readonly confirmLabel: string;
  readonly cancelLabel: string;
  readonly onConfirm: () => void;
}

export interface ActionMenuItem {
  readonly id: string;
  readonly title: string;
  readonly meta?: string;
  readonly lead?: ReactNode;
  /** The one already open. Drawn quieter than the arrow keys' highlight. */
  readonly current?: boolean;
  readonly tone?: "default" | "danger";
  readonly onSelect: () => void;
  readonly remove?: ActionMenuRemove;
}

export interface ActionMenuSection {
  readonly id: string;
  readonly label?: string;
  readonly items: readonly ActionMenuItem[];
}

export interface ActionMenuProps {
  readonly name: string;
  readonly sections: readonly ActionMenuSection[];
  readonly labels: {
    readonly title: string;
    readonly closeList: string;
    readonly emptyTitle: string;
    readonly emptyHint?: string;
  };
  readonly group?: MenuGroup;
  /** Goes on the box that holds the trigger and the panel, which also carries
   *  `data-open` so a consumer's stylesheet can dress its own trigger. */
  readonly className?: string;
  /** Merged into the box that holds the trigger and the panel. Inline, because
   *  a Tailwind class written in a package is emitted only by accident. */
  readonly style?: CSSProperties;
  readonly side?: "start" | "end";
  readonly width?: number | string;
  readonly body?: "auto" | "menu" | "sheet";
  readonly sheetMaxWidth?: number;
  readonly renderTrigger?: (props: MenuTriggerRenderProps) => ReactNode;
  readonly triggerValue?: ReactNode;
  readonly defaultOpen?: boolean;
  readonly openNonce?: number;
}

export function ActionMenu({
  name,
  sections,
  labels,
  group,
  className,
  style,
  side = "end",
  width = 320,
  body = "auto",
  sheetMaxWidth = 639,
  renderTrigger,
  triggerValue,
  defaultOpen,
  openNonce,
}: ActionMenuProps) {
  useMenuStyles();
  const dir = useDocumentDir();
  const narrow = useNarrowViewport(sheetMaxWidth);
  const sheet = body === "sheet" || (body === "auto" && narrow);

  const surfaceRef = useRef<HTMLElement | null>(null);
  const dd = useDropdown(name, { group, surfaceRef, defaultOpen, openNonce });
  const { mounted, state, ref: presenceRef } = usePresence(dd.open, sheet ? 260 : undefined);

  // One flat run for the arrow keys even though the eye sees several groups.
  const flat = sections.flatMap((section) => section.items);
  const { active, setActive, listRef } = useRoving(dd.open, flat.length, 0);

  /** Which row is currently asking before it deletes itself. */
  const [asking, setAsking] = useState<string | null>(null);
  useEffect(() => {
    if (!dd.open) setAsking(null);
  }, [dd.open]);

  const run = (index: number) => {
    const item = flat[index];
    if (!item) return;
    item.onSelect();
    dd.setOpen(false);
  };

  const triggerProps: MenuTriggerRenderProps = {
    ref: dd.triggerRef,
    open: dd.open,
    chosen: false,
    label: labels.title,
    buttonProps: {
      type: "button",
      onClick: dd.toggle,
      "aria-haspopup": "menu",
      "aria-expanded": dd.open,
    },
  };

  let index = -1;

  return (
    <div
      ref={dd.ref}
      className={className}
      data-open={dd.open ? "true" : undefined}
      style={{ position: "relative", display: "inline-block", ...style }}
      onKeyDown={menuKeys({
        open: dd.open,
        setOpen: dd.setOpen,
        count: flat.length,
        active,
        setActive,
        commit: run,
      })}
    >
      {renderTrigger ? (
        renderTrigger(triggerProps)
      ) : (
        <MenuTrigger
          ref={dd.triggerRef}
          open={dd.open}
          value={triggerValue ?? labels.title}
          ariaHasPopup="menu"
          onClick={dd.toggle}
        />
      )}

      {mounted ? (
        <MenuSurface
          presenceRef={presenceRef}
          surfaceRef={surfaceRef}
          state={state}
          instant={dd.instant}
          sheet={sheet}
          side={side}
          width={width}
          role={sheet ? "dialog" : "menu"}
          ariaLabel={labels.title}
          title={labels.title}
          closeLabel={labels.closeList}
          onClose={dd.close}
          dir={dir}
        >
          {flat.length === 0 ? (
            <EmptyRow title={labels.emptyTitle} hint={labels.emptyHint} />
          ) : (
            <MenuScroll
              listRef={listRef}
              max={sheet ? 340 : 300}
              role={sheet ? "menu" : undefined}
              ariaLabel={sheet ? labels.title : undefined}
            >
              {sections.map((section, sectionIndex) => (
                <div key={section.id}>
                  {sectionIndex > 0 ? <MenuRule /> : null}
                  {section.label ? <MenuLabel>{section.label}</MenuLabel> : null}
                  {section.items.map((item) => {
                    index += 1;
                    const at = index;
                    return (
                      <ActionRow
                        key={item.id}
                        item={item}
                        index={at}
                        active={active === at}
                        large={sheet}
                        asking={asking === item.id}
                        onHover={() => setActive(at)}
                        onRun={() => run(at)}
                        onAsk={() => setAsking(item.id)}
                        onDrop={() => setAsking(null)}
                      />
                    );
                  })}
                </div>
              ))}
            </MenuScroll>
          )}
        </MenuSurface>
      ) : null}
    </div>
  );
}

function ActionRow({
  item,
  index,
  active,
  large,
  asking,
  onHover,
  onRun,
  onAsk,
  onDrop,
}: {
  item: ActionMenuItem;
  index: number;
  active: boolean;
  large: boolean;
  asking: boolean;
  onHover: () => void;
  onRun: () => void;
  onAsk: () => void;
  onDrop: () => void;
}) {
  const danger = item.tone === "danger";
  const ink = danger ? RED : NAVY;
  const background = active
    ? danger
      ? "rgba(155,44,44,0.09)"
      : ROW_ACTIVE
    : item.current
      ? ROW_CURRENT
      : "transparent";

  if (asking && item.remove) {
    /* The row keeps its own title while it asks. A confirm that replaces the
       title makes the traveller work out which one they are about to lose,
       which is the failing the browser dialog this replaces also had. */
    return (
      <div
        data-row={index}
        data-active={active ? "true" : undefined}
        onMouseEnter={onHover}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          minHeight: large ? 46 : 38,
          padding: "6px 8px 6px 10px",
          borderRadius: 12,
          background: "rgba(155,44,44,0.07)",
        }}
      >
        <span style={{ display: "flex", flex: 1, minWidth: 0, flexDirection: "column", gap: 2 }}>
          <span
            style={{
              fontSize: large ? 14 : 13,
              fontWeight: 600,
              color: NAVY,
              overflowWrap: "break-word",
            }}
          >
            {item.title}
          </span>
          <span style={{ fontSize: 11.5, fontWeight: 700, color: RED }}>
            {item.remove.question}
          </span>
        </span>
        <button
          type="button"
          onClick={onDrop}
          className="mj-ui-press"
          style={{
            minHeight: 24,
            borderRadius: 999,
            border: "none",
            cursor: "pointer",
            background: "rgba(17,14,57,0.06)",
            color: NAVY,
            padding: "4px 10px",
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {item.remove.cancelLabel}
        </button>
        <button
          type="button"
          onClick={() => {
            item.remove?.onConfirm();
            onDrop();
          }}
          className="mj-ui-press"
          style={{
            minHeight: 24,
            borderRadius: 999,
            border: "none",
            cursor: "pointer",
            background: RED,
            color: "#fff",
            padding: "4px 12px",
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          {item.remove.confirmLabel}
        </button>
      </div>
    );
  }

  const row = (
    <button
      type="button"
      role="menuitem"
      onClick={onRun}
      className="mj-ui-row mj-ui-press"
      style={{
        display: "flex",
        flex: 1,
        minWidth: 0,
        alignItems: "center",
        gap: 10,
        minHeight: large ? 46 : 38,
        padding: large ? "10px 12px" : "8px 10px",
        borderRadius: 12,
        border: "none",
        cursor: "pointer",
        textAlign: "start",
        background: item.remove ? "transparent" : background,
      }}
    >
      {item.lead}
      <span style={{ display: "flex", minWidth: 0, flex: 1, flexDirection: "column", gap: 2 }}>
        <span
          style={{
            fontSize: large ? 14 : 13,
            fontWeight: item.current || danger ? 700 : 500,
            color: ink,
            overflowWrap: "break-word",
          }}
        >
          {item.title}
        </span>
        {item.meta ? <span style={{ fontSize: 11, color: MUTED }}>{item.meta}</span> : null}
      </span>
    </button>
  );

  if (!item.remove) {
    return (
      <div data-row={index} data-active={active ? "true" : undefined} onMouseEnter={onHover}>
        {row}
      </div>
    );
  }

  return (
    <div
      data-row={index}
      data-active={active ? "true" : undefined}
      onMouseEnter={onHover}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 4,
        paddingInlineEnd: 4,
        borderRadius: 12,
        background,
      }}
    >
      {row}
      {/* Its own button, beside the row rather than inside it. */}
      <button
        type="button"
        onClick={onAsk}
        aria-label={item.remove.label}
        className="mj-ui-press"
        style={{
          display: "grid",
          placeItems: "center",
          width: 28,
          height: 28,
          flexShrink: 0,
          borderRadius: 999,
          border: "none",
          cursor: "pointer",
          background: "transparent",
          color: MUTED,
        }}
      >
        <Icon name="ri-delete-bin-line" size={14} aria-hidden="true" />
      </button>
    </div>
  );
}
